import React, { useCallback } from 'react'

import { reportError } from 'src/lib/js/clientError'

import { t } from 'src/translations'
import classNames from 'classnames'

import {
  AlertModalSource,
  useAlertModalContext
} from 'shared/components/common/alert_modal/AlertModal'
import { usePopoverContext } from 'shared/components/common/popover/PopoverContext'
import { REQUEST_FAILURE_MESSAGE } from 'shared/js/constants'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'

import { CatalogProductOption } from 'src/apollo/onlineOrdering'
import { CART_POPOVER_CONTEXT_KEY } from 'public/components/default_template/online_ordering/cart/CartModal'
import QuantitySelector from 'public/components/default_template/online_ordering/item_modal/QuantitySelector'
import { useDisplayPageContext } from 'public/components/default_template/DisplayPageContext'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import {
  ItemWithTimeBasedRule,
  useTimeBasedRules
} from 'public/components/online_ordering/TimeBasedRuleContext'
import { ItemSource } from '../../../online_ordering/cart/cartUtils'
import { BrandedButton } from 'src/shared/components/common/button'
import { ButtonType } from 'src/apollo/sites'
import { useA11y } from 'public/components/online_ordering/useA11y'

type AddToCartSectionProps = {
  totalItemPrice: number
  setShowFooter: (showFooter: boolean) => void
  outOfStock?: boolean
  hasVariationOptions?: boolean
  hasUnselectedVariationOptions?: boolean
  setVariationOptionsInvalid?: (invalid: boolean) => void
  unselectedVariationOptions?: CatalogProductOption[]
}
export const AddToCartSection = ({
  totalItemPrice,
  setShowFooter,
  outOfStock,
  hasVariationOptions = false,
  hasUnselectedVariationOptions = false,
  setVariationOptionsInvalid,
  unselectedVariationOptions = []
}: AddToCartSectionProps) => {
  const {
    displayedModifier,
    quantity,
    setQuantity,
    validate,
    invalidRequiredGroups
  } = useModifierContext()
  const { announce } = useA11y()
  const { addToCart, loadingCart } = useCart()
  const { fulfillmentData } = useFulfillment()
  const { openAlertModal } = useAlertModalContext()
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const context = usePopoverContext(CART_POPOVER_CONTEXT_KEY)
  const { channelGuid } = useDisplayPageContext()

  const {
    verifyItemIsFulfillable,
    verifyNoConflictingItemsInCart,
    timeBasedRulesMap
  } = useTimeBasedRules()
  const isDisabled = !quantity || loadingCart || outOfStock
  const fulfillmentType = fulfillmentData?.cartFulfillmentData.fulfillmentType
  const futureSchedules = fulfillmentData?.scheduleData.futureScheduleDates

  const onSubmit = useCallback(async () => {
    setVariationOptionsInvalid?.(false)

    if (!displayedModifier) {
      return
    }

    // Validate variation options first (if applicable)
    if (hasVariationOptions && hasUnselectedVariationOptions) {
      setVariationOptionsInvalid?.(true)
      setTimeout(() => {
        setVariationOptionsInvalid?.(false)
      }, 5000)
      if (unselectedVariationOptions.length > 0) {
        announce(
          [
            t('modifiers-section.required-modifiers-error'),
            ...unselectedVariationOptions.map((option) => option.name)
          ].join(' '),
          'assertive'
        )
      }
      return
    }

    // Then validate modifiers
    if (!validate()) {
      if (invalidRequiredGroups && invalidRequiredGroups.length > 0) {
        announce(
          [
            t('modifiers-section.required-modifiers-error'),
            ...invalidRequiredGroups.map((group) => group.name)
          ].join(' '),
          'assertive'
        )
      }
      return
    }

    let errorType = null

    const timeBasedRules =
      timeBasedRulesMap[displayedModifier.modifier.itemGuid]
    try {
      const item = {
        itemGuid: displayedModifier.modifier.itemGuid,
        preorderRule: timeBasedRules?.preorderRule,
        leadTimeRule: timeBasedRules?.leadTimeRule,
        pickupWindowRule: timeBasedRules?.pickupWindowRule,
        name: displayedModifier.name,
        modifier: displayedModifier,
        quantity
      } as ItemWithTimeBasedRule
      if (!verifyNoConflictingItemsInCart(item)) {
        // opens ItemResolutionModal with the conflicting item
        return
      }

      const trackData = {
        quantity,
        unitOfMeasure: displayedModifier.unitOfMeasure,
        timeBasedRule: timeBasedRules?.leadTimeRule
          ? 'Minimum lead time'
          : timeBasedRules?.preorderRule
          ? 'Preorder'
          : timeBasedRules?.pickupWindowRule
          ? 'Pickup window'
          : null
      }

      errorType = await addToCart(
        displayedModifier,
        quantity,
        fulfillmentData?.cartFulfillmentData,
        trackData,
        channelGuid,
        ItemSource.Menu
      )
    } catch (error) {
      openAlertModal(
        REQUEST_FAILURE_MESSAGE,
        undefined,
        AlertModalSource.PRODUCT_DISPLAY
      )
      reportError('Error adding cart item.', error)
      errorType = 'CartModificationError'
    }
    if (errorType !== 'CartModificationError') {
      //only open the cart modal for desktop
      if (!isMobile) {
        context?.open()
      } else {
        setShowFooter(true)
      }
    }
  }, [
    displayedModifier,
    hasVariationOptions,
    hasUnselectedVariationOptions,
    setVariationOptionsInvalid,
    validate,
    timeBasedRulesMap,
    quantity,
    verifyNoConflictingItemsInCart,
    addToCart,
    fulfillmentData?.cartFulfillmentData,
    channelGuid,
    openAlertModal,
    unselectedVariationOptions,
    invalidRequiredGroups,
    announce,
    isMobile,
    context,
    setShowFooter
  ])

  if (
    displayedModifier?.isRoot &&
    fulfillmentType &&
    !verifyItemIsFulfillable(
      displayedModifier.modifier.itemGuid,
      fulfillmentType,
      futureSchedules ?? []
    )
  ) {
    return (
      <div className='addToCartRow'>
        <BrandedButton
          variant={ButtonType.Primary}
          disabled
          className='modalButton addToCartButton centered'
        >
          {t('add-to-cart-section.item-not-currently-available')}
        </BrandedButton>
      </div>
    )
  }

  return (
    <div>
      {displayedModifier?.usesFractionalQuantity && !outOfStock ? (
        <div className='fractionalQuantity modSection'>
          <div className='title'>{t('add-to-cart-section.quantity')}</div>
          <div className='quantity'>
            <input
              type='number'
              inputMode='decimal'
              disabled={loadingCart}
              name='fractionalQuantityInput'
              placeholder='0.00'
              defaultValue={quantity.toFixed(2)}
              value={quantity}
              onChange={(e) => setQuantity(parseFloat(e.target.value))}
            />
            <label htmlFor='fractionalQuantityInput'>
              {displayedModifier?.unitOfMeasure?.toLowerCase()}
            </label>
          </div>
          <i>
            {displayedModifier?.price}/
            {displayedModifier?.unitOfMeasure?.toLowerCase()}
          </i>
        </div>
      ) : null}
      <div className='addToCartRow'>
        <BrandedButton
          onClick={onSubmit}
          type='button'
          variant={ButtonType.Primary}
          disabled={isDisabled}
          className={classNames('modalButton addToCartButton centered', {
            outOfStock
          })}
          data-testid='pdp-add-to-cart-cta'
        >
          {outOfStock
            ? t('add-to-cart-section.out-of-stock')
            : t('add-to-cart-section.add-to-cart')}
        </BrandedButton>

        {!displayedModifier?.usesFractionalQuantity && !outOfStock ? (
          <div
            className='actionWrapper'
            data-testid='quantity-selector-wrapper'
          >
            <QuantitySelector
              min={1}
              canIncrease={true}
              disabled={isDisabled}
              defaultValue={quantity}
              onQuantityChange={setQuantity}
              totalItemPrice={totalItemPrice}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
